import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import {
    getTheme2FormFieldsCss,
    getTheme2FormPageDarkSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import UserDefinedSecurityTabs from "../../../components/UserDefined/UserDefinedSecurityTabs";
import UserDefinedFieldsTabs from "../../../components/UserDefined/UserDefinedFieldsTabs";
import AprTab from "./AprTab";
import InternetMktgTab from "./InternetMktgTab";
import InstitutionInventoryTab from "components/UserDefined/InstitutionInventoryTab";
import classNames from "classnames";

const Institution = ({ activeColor = "blue", account, embedded = false, shellThemeVariant = "1" }) => {
    const { t } = useTranslation();

    // Check if user has permission to view user defined fields
    const hasUserDefinedReadPermission = account?.permissions?.settings_user_defined_read === true;

    const allTabs = [
        t('institution.inventory'),
        t('institution.inHouse'),
        t('institution.internetMktg'),
        //  t('institution.security'),
        t('institution.userDefinedFields'),
    ];

    // Filter tabs based on permission
    const tabs = hasUserDefinedReadPermission ? allTabs : allTabs.filter(tab => tab !== t('institution.userDefinedFields'));

    const [activeTab, setActiveTab] = useState(t('institution.inventory'));
    const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 992);
    const tabScrollRef = useRef(null);
    const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
    const useHorizontalMobileTabs = isMobile;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const isTheme2 = shellThemeVariant === "2";
    const selectStyles = useMemo(
        () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const theme2FieldsCss = useMemo(
        () =>
            isTheme2
                ? `${getTheme2FormFieldsCss({
                      scopeSelector: ".institution-page.institution-page--theme2",
                      fieldsSelector: ".institution-form-fields",
                  })}
                  .institution-page.institution-page--theme2 .nav-link {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .institution-page.institution-page--theme2 table th,
                  .institution-page.institution-page--theme2 table td {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .institution-page.institution-page--theme2 .card-title,
                  .institution-page.institution-page--theme2 .institution-inventory-card__title,
                  .institution-page.institution-page--theme2 .institution-form-fields .card .card-header {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .institution-page.institution-page--theme2 .institution-inventory-card__description {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .institution-page.institution-page--theme2 label {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .institution-page.institution-page--theme2 .institution-form-fields .react-select__input-container input {
                    min-width: 4px !important;
                    opacity: 1 !important;
                  }`
                : "",
        [isTheme2]
    );

    const checkTabScroll = useCallback(() => {
        const el = tabScrollRef.current;
        if (!el) {
            setCanScrollTabsRight(false);
            return;
        }
        setCanScrollTabsRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    }, []);

    useEffect(() => {
        if (!useHorizontalMobileTabs) {
            setCanScrollTabsRight(false);
            return undefined;
        }
        const runCheck = () => {
            requestAnimationFrame(checkTabScroll);
        };
        runCheck();
        const scrollEl = tabScrollRef.current;
        scrollEl?.addEventListener("scroll", checkTabScroll);
        window.addEventListener("resize", runCheck);
        return () => {
            scrollEl?.removeEventListener("scroll", checkTabScroll);
            window.removeEventListener("resize", runCheck);
        };
    }, [checkTabScroll, tabs.length, useHorizontalMobileTabs]);

    const handleTabScrollRight = useCallback(() => {
        if (tabScrollRef.current && canScrollTabsRight) {
            tabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
        }
    }, [canScrollTabsRight]);

    const handleTabClick = useCallback(
        (tab) => (e) => {
            e.preventDefault();
            setActiveTab(tab);
        },
        []
    );

    const getTabDisplayName = (tab) => {
        return tab;
    };

    const renderTabLink = (tab) => (
        <NavItem key={tab} style={useHorizontalMobileTabs ? { flex: "0 0 auto" } : undefined}>
            <NavLink
                href="#"
                className={activeTab === tab ? "active font-weight-bold" : "text-muted"}
                style={{
                    background: "none",
                    border: "none",
                    position: "relative",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    color: activeTab === tab ? activeColorStyle.borderColor : undefined,
                    fontWeight: activeTab === tab ? 700 : 500,
                }}
                onClick={handleTabClick(tab)}
            >
                {getTabDisplayName(tab)}
                {activeTab === tab && (
                    <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                )}
            </NavLink>
        </NavItem>
    );

    const tabsNav = useHorizontalMobileTabs ? (
        <div className="d-flex align-items-center mb-0 institution-detail-tabs detail-mobile-tabs w-100" style={{ minWidth: 0 }}>
            <div
                ref={tabScrollRef}
                className="flex-grow-1 channel-tabs-scroll"
                style={{ minWidth: 0 }}
            >
                <Nav
                    tabs
                    className="border-0"
                    style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        minWidth: "max-content",
                        width: "auto",
                        paddingLeft: 0,
                        paddingRight: 0,
                    }}
                >
                    {tabs.map(renderTabLink)}
                </Nav>
            </div>
            {canScrollTabsRight && (
                <button
                    type="button"
                    className="btn btn-link p-0 flex-shrink-0"
                    onClick={handleTabScrollRight}
                    style={{
                        color: activeColorStyle.borderColor,
                        lineHeight: 1,
                    }}
                    aria-label={t("common.scrollTabs")}
                >
                    <i className="tim-icons icon-minimal-right" style={{ fontSize: 20 }} />
                </button>
            )}
        </div>
    ) : (
        <div className="d-flex align-items-center justify-content-between mb-0 overflow-auto institution-detail-tabs w-100">
            <Nav
                tabs
                className="border-0 flex-nowrap"
                style={{ borderBottom: "none", width: "max-content", minWidth: "100%" }}
            >
                {tabs.map(renderTabLink)}
            </Nav>
        </div>
    );

    return (
        <div
            className={classNames(
                !embedded && "content",
                "institution-page",
                {
                    "institution-page--theme2": isTheme2,
                    "theme2-admin-content-compact": isTheme2 && !embedded,
                    "theme2-institution-inset": isTheme2 && !embedded,
                }
            )}
            style={{
                minHeight: embedded ? undefined : "80vh",
                paddingTop: embedded ? 0 : shellThemeVariant === "2" ? undefined : "5rem",
                width: "100%",
            }}
        >
            {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
            <div style={{ width: '100%' }}>
                <div className="w-100">
                    {tabsNav}
                    <TabContent
                        activeTab={activeTab}
                        className={classNames({ "institution-form-fields": isTheme2 })}
                    >
                        <TabPane tabId={activeTab}>
                            {activeTab === t('institution.inventory') && (
                                <InstitutionInventoryTab
                                    permissions={account?.permissions}
                                    activeColorStyle={activeColorStyle}
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    selectStyles={selectStyles}
                                />
                            )}
                            {activeTab === t('institution.inHouse') && (
                                <AprTab activeColorStyle={activeColorStyle} isTheme2={isTheme2} />
                            )}
                            {activeTab === t('institution.internetMktg') && (
                                <InternetMktgTab activeColorStyle={activeColorStyle} isTheme2={isTheme2} />
                            )}
                            {/* {activeTab === t('institution.security') && <UserDefinedSecurityTabs permissions={account?.permissions} />} */}
                            {activeTab === t('institution.userDefinedFields') && hasUserDefinedReadPermission && (
                                <UserDefinedFieldsTabs
                                    permissions={account?.permissions}
                                    activeColorStyle={activeColorStyle}
                                    isTheme2={isTheme2}
                                />
                            )}
                        </TabPane>
                    </TabContent>
                </div>
            </div>
        </div>);
};

export default Institution;